import {
    filter as _filter,
    includes as _includes,
    trim as _trim,
} from 'lodash';
import { StoreConfig } from 'src/app/shared/config/store-config';

export type OrderPlatform = StoreConfig['platform'];

// Which marketplace an order number could have come from, read off its shape
// alone. This is what makes naming the shop optional on the receipt page: the
// number identifies the platform, and every shop on that platform is then
// asked at once.
//
// The three formats, as the platforms actually emit them:
//   shopee   2607098WBX8C5E       YYMMDD then 8 alphanumerics — 14 characters
//   lazada   1108939863709406     all digits, 16 or fewer
//   tiktok   584727114725689265   all digits, 18 or 19
//
// Only the two digit formats can collide, so the ranges below overlap at 17 —
// a length neither has been seen at — and a Shopee SN whose 8 trailing
// characters happen to be digits answers both shopee and lazada. Ambiguity is
// not an error: it only means more shops are asked, and the one holding the
// order still wins.
const SHOPEE_ORDER_NO = /^[0-9]{6}[0-9A-Z]{8}$/;
const DIGITS_ONLY = /^[0-9]+$/;
const LAZADA_MAX_DIGITS = 17;
const TIKTOK_MIN_DIGITS = 17;

export const candidatePlatforms = (orderNo: string): OrderPlatform[] => {
    // Uppercased for matching only — what is sent to the platform stays as
    // the operator typed it.
    const value = _trim(orderNo).toUpperCase();
    const platforms: OrderPlatform[] = [];
    if (SHOPEE_ORDER_NO.test(value)) {
        platforms.push('shopee');
    }
    if (DIGITS_ONLY.test(value)) {
        if (value.length <= LAZADA_MAX_DIGITS) {
            platforms.push('lazada');
        }
        if (value.length >= TIKTOK_MIN_DIGITS) {
            platforms.push('tiktok');
        }
    }
    return platforms;
};

// The shops worth asking for an order number. A shape that names no platform
// falls back to all of them rather than refusing: a lookup that finds nothing
// costs the operator no more than the error it would replace, and the number
// may simply be a format not seen here yet.
export const candidateStores = (
    orderNo: string,
    stores: StoreConfig[],
): StoreConfig[] => {
    const platforms = candidatePlatforms(orderNo);
    return platforms.length
        ? _filter(stores, (store: StoreConfig) =>
              _includes(platforms, store.platform),
          )
        : stores;
};
